import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import LogoTitle from "@Components/LogoTitle/LogoTitle";
import FormPageTemplate from "@Pages/FormPageTemplate/FormPageTemplate";
import loginLeftPage from "@Assets/BG2.jpg";
import themeCreator from "@Themes/themeCreator";
import { useParams } from "react-router-dom";
import axiosInstance from "@API/axios";
import { USER_RESET_PASS } from "@API/APIRoutes";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { setSnackbar } from "@Redux/snackBarSlice";
import ChangePasswordScreenStyles from "./ChangePasswordScreenStyles";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  IconButton,
  InputAdornment,
} from "@mui/material";

export default function ChangePasswordScreen() {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const password = watch("password"); // Watch the first password field

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const customTheme = themeCreator();
  const { email, otp } = useParams();

  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const onVerifySubmit = (data) => {
    let resetPasswordRequest = {
      email: decodeURIComponent(email),
      otp: otp,
      password: data.password,
    };
    setLoading(true);
    axiosInstance
      .post(USER_RESET_PASS, resetPasswordRequest)
      .then((response) => {
        dispatch(
          setSnackbar({
            open: true,
            message: "Password reset successfully!",
            error: false,
          })
        );
        navigate("/");
      })
      .catch((err) => {
        setLoading(false);
        dispatch(
          setSnackbar({
            open: true,
            message: "Internal Server Error.",
            error: true,
          })
        );
      });
  };

  useEffect(() => {
    if (email == null || otp == null) {
      navigate("/");
    }
  }, []);

  const handleToggleNewPassword = () => setShowNewPassword((prev) => !prev);
  const handleToggleConfirmPassword = () =>
    setShowConfirmPassword((prev) => !prev);

  const rightGridContent = (
    <>
      <form onSubmit={handleSubmit(onVerifySubmit)} className="formWrapper">
        <Grid
          container
          spacing={2}
          className="inputContainer"
          justifyContent="flex-end"
        >
          <Grid item xs={12} justifyContent="flex-start">
            <LogoTitle isDisabled />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1" sx={{ mb: 2, mt: 2 }} align="left">
              Enter New Password
            </Typography>
            <TextField
              type={showNewPassword ? "text" : "password"}
              variant="outlined"
              fullWidth
              placeholder="Enter New Password"
              {...register("password", {
                required: "Password is required",
                minLength: {
                  value: 8,
                  message: "Password must be at least 8 characters",
                },
              })}
              error={!!errors.password}
              helperText={errors.password?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleToggleNewPassword} edge="end">
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Confirm New Password Field */}
          <Grid item xs={12}>
            <Typography variant="body1" sx={{ mb: 2, mt: 2 }} align="left">
              Confirm New Password
            </Typography>
            <TextField
              type={showConfirmPassword ? "text" : "password"}
              variant="outlined"
              fullWidth
              placeholder="Confirm New Password"
              {...register("confirmPassword", {
                required: "Please confirm your password",
                validate: (value) =>
                  value === password || "Passwords do not match",
              })}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleToggleConfirmPassword}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Box className="submissionBox">
            <Button
              type="submit"
              variant="contained"
              className="submissionButton"
              size="large"
              disabled={loading}
            >
              {!loading ? (
                "Reset"
              ) : (
                <LoadingComponent size="2em" thickness={5} color="white" />
              )}
            </Button>
          </Box>
        </Grid>
      </form>
    </>
  );

  return (
    <ChangePasswordScreenStyles>
      <FormPageTemplate
        rightGridForm={rightGridContent}
        leftGridTitle="Change Password Page"
        textColor={customTheme.palette.white.main}
        leftGridBG={loginLeftPage}
        backUrl={"/"}
      />
    </ChangePasswordScreenStyles>
  );
}
